import * as React from 'react';
import { atom, DefaultValue, selectorFamily } from 'recoil';

import type { AdaptiveContentBuilderStateContextProps, SiteVisitorSegment } from './types';

/**
 * Atom storing the current visitor claims being previewed in the app for a site.
 */
export const siteVisitorSegmentsAtom = atom<{
    [siteId: string]: SiteVisitorSegment;
}>({
    key: 'siteVisitorClaimsAtom',
    default: {},
});

/**
 * Selector to get the visitor claims for a site.
 */
export const siteVisitorSegmentSelector = selectorFamily<SiteVisitorSegment | null, string>({
    key: 'siteVisitorSegmentSelector',
    get:
        (siteId) =>
        ({ get }) => {
            const siteClaims = get(siteVisitorSegmentsAtom);
            return siteClaims[siteId] ?? null;
        },
    set:
        (siteId) =>
        ({ set, get }, newValue) => {
            set(siteVisitorSegmentsAtom, {
                ...get(siteVisitorSegmentsAtom),
                [siteId]: newValue instanceof DefaultValue ? null : newValue,
            });
        },
});

export const AdaptiveContentBuilderStateContext = React.createContext<
    AdaptiveContentBuilderStateContextProps | undefined
>(undefined);

export function useAdaptiveContentBuilderStateContext() {
    const context = React.useContext(AdaptiveContentBuilderStateContext);
    if (!context) {
        throw new Error(
            'useAdaptiveContentBuilderContext must be used within AdaptiveContentBuilderDialog'
        );
    }
    return context;
}
